import { useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import type { AuthUser } from "../../types";
import { useAuth } from "../../hooks/useAuth";
import { ThemeToggle } from "../ThemeToggle";

const ROLE_LABELS: Record<AuthUser["role"], string> = {
  SUPER_ADMIN: "Super Admin",
  EVENT_MANAGER: "Event Manager",
  EVENT_STAFF: "Event Staff",
};

export function Topbar({ user, onMenuClick }: { user: AuthUser; onMenuClick: () => void }): JSX.Element {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async (): Promise<void> => {
    try {
      await logout();
      navigate("/login");
    } catch {
      toast.error("Failed to log out. Please try again.");
    }
  };

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200 bg-white/80 px-4 backdrop-blur-md transition-colors duration-200 dark:border-slate-700 dark:bg-slate-800/80 lg:px-6">
      <button
        type="button"
        className="rounded-md p-2 text-slate-500 transition-colors hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700 lg:hidden"
        onClick={onMenuClick}
        aria-label="Toggle menu"
      >
        &#9776;
      </button>
      <div className="hidden lg:block" />
      <div className="flex items-center gap-1.5">
        <ThemeToggle />
        <div className="relative">
          <button
            type="button"
            className="flex items-center gap-3 rounded-md px-2 py-1.5 transition-colors hover:bg-slate-100 dark:hover:bg-slate-700"
            onClick={() => setMenuOpen((v) => !v)}
          >
            {user.avatarUrl ? (
              <img src={user.avatarUrl} alt="" className="h-8 w-8 rounded-full ring-2 ring-white dark:ring-slate-800" />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-gradient text-sm font-semibold text-white shadow-sm">
                {user.name.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="hidden text-left sm:block">
              <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{user.name}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">{ROLE_LABELS[user.role]}</p>
            </div>
          </button>
          {menuOpen && (
            <div className="absolute right-0 mt-2 w-48 origin-top-right animate-scale-in rounded-lg border border-slate-200 bg-white py-1 shadow-card-hover dark:border-slate-700 dark:bg-slate-800">
              <div className="border-b border-slate-100 px-4 py-2 dark:border-slate-700">
                <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{user.name}</p>
                <p className="truncate text-xs text-slate-500 dark:text-slate-400">{user.email}</p>
              </div>
              <button
                type="button"
                onClick={handleLogout}
                className="block w-full px-4 py-2 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
